<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-skeleton-loader v-if="$store.state.invoices.retrieveLoader" transition="fade-transition" type="table" />
      <template v-else>
        <v-col v-if="!list.length" class="text-center py-5">
          <v-icon class="d-block" color="secondary" size="70">mdi-file</v-icon>
          <span class="d-block subtitle-1">No existen documentos</span>
          <v-btn class="my-5" v-if="showCreate" outlined>Importar un documento</v-btn>
        </v-col>
        <template v-else>
          <v-data-table :headers="computedHeaders" :items="list" :items-per-page="pagination" hide-default-header disable-sort mobile-breakpoint hide-default-footer>
            <!-- header -->
            <template v-slot:header="{ props: { headers } }">
              <VTableHeaders :headers="headers" @sort="setSort" />
            </template>
            <!-- end header -->
            <template v-slot:[`item.customer`]="{item}">
              <div class="text-left py-4">
                <span class="d-block secondary--text font-weight-medium">{{item.customer.name}}</span>
              </div>
            </template>
            <template v-slot:[`item.document_type`]="{item}">
              <span class="d-block">{{item.document_type_display}}</span>
            </template>
            <template v-slot:[`item.subscriptions`]="{item}">
              <v-chip v-if="item.subscriptions.length" color="lightBlue" text-color="primary" small>En uso <v-icon class="ml-1" x-small>mdi-check</v-icon></v-chip>
              <v-chip v-else color="defaultGrey" text-color="secondary" small>Sin uso <v-icon class="ml-1" x-small>mdi-clock</v-icon></v-chip>
            </template>
            <template v-slot:[`item.serial`]="{item}">
              <span class="d-block">{{item.serial}}</span>
            </template>
            <template v-slot:[`item.date_issued`]="{item}">
              <span>{{item.date_issued | date}}</span>
            </template>
            <template v-slot:[`item.valid_until`]="{item}">
              <span>{{item.valid_until | date}}</span>
            </template>
            <template v-slot:[`item.actions`]="{item}">
              <v-menu close-on-click close-on-content-click left offset-y :nudge-bottom="10" transition="slide-y-transition">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="ml-2" v-bind="attrs" v-on="on" min-width="34" max-width="34" color="secondary" outlined retain-focus-on-click><v-icon color="secondary">mdi-dots-horizontal</v-icon></v-btn>
                </template>
                <v-list class="pa-0">
                  <v-list-item v-if="actions.includes('view_pdf')" @click="openPdf(item)" class="px-5 py-1" :ripple="false">
                    <v-list-item-title class="text-right subtitle-2 font-weight-medium">Ver PDF</v-list-item-title>
                  </v-list-item>

                  <v-list-item v-if="actions.includes('disconnect') && subscriptionId !== null" class="px-5 py-1" :ripple="false" @click="disconnectSupportingDocument(item)">
                    <v-list-item-title class="text-right subtitle-2 red--text font-weight-medium">Desconectar</v-list-item-title>
                  </v-list-item>

                  <v-list-item v-if="actions.includes('view_subscriptions')" class="px-5 py-1" @click="openAssociatedSubscriptionsDialog(item)" :ripple="false" >
                    <v-list-item-title class="text-right subtitle-2 font-weight-medium">Ver suscripciones</v-list-item-title>
                  </v-list-item>

                  <v-list-item v-if="actions.includes('view_customer')" class="px-5 py-1" :ripple="false" :to="{ name: 'CustomerRetrieveUpdate', params: { id: item.customer.id } }">
                    <v-list-item-title class="text-right subtitle-2 font-weight-medium">Ver cliente</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
          <template v-if="showPagination || showAll">
            <v-divider />
            <v-row class="mx-0" align="center">
              <v-col cols="12" class="text-center" v-if="showAll">
                <v-btn :to="{name: 'InvoiceList', query: showCriteria}" outlined color="secondary">Ver todas las facturas</v-btn>
              </v-col>
              <template v-if="showPagination">
                <v-col cols="6">
                  <span class="body-2 text--secondary">{{count}} registros</span>
                </v-col>
                <v-col cols="6" class="text-right">
                  <v-pagination v-model="page" :length="totalPages" total-visible="6" />
                </v-col>
              </template>
            </v-row>
          </template>
        </template>
      </template>
    </v-col>

    <v-dialog v-model="dialogAssociatedSubscriptions" width="620" persistent no-click-animation>
      <v-card>
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Suscripciones asociadas</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn @click="closeAssociatedSubscriptionsDialog" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-2 background">
          <v-skeleton-loader v-if="loadingAssociatedSubscriptions" type="list-item-three-line@4" />
          <template v-else-if="associatedSubscriptions.length">
            <v-card v-for="s in associatedSubscriptions" :key="`associated-${s.id}`" class="ma-3" :to="{ name: 'SubscriptionRetrieve', params: { id: s.id } }">
              <v-list class="py-0">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      <v-chip v-if="s.status === 'active'" color="lightBlue" text-color="primary" small>Activo <v-icon class="ml-1" x-small>mdi-check</v-icon></v-chip>
                      <v-chip v-else-if="s.status === 'paused'" color="defaultGrey" text-color="secondary" small>Pausado<v-icon class="ml-1" x-small>mdi-pause</v-icon></v-chip>
                      <v-chip v-else-if="s.status === 'cancelled'" color="defaultGrey" text-color="secondary" small>Cancelado<v-icon class="ml-1" x-small>mdi-close</v-icon></v-chip>
                      <v-chip v-else-if="s.status === 'finalized'" color="defaultGrey" text-color="secondary" small>Finalizado<v-icon class="ml-1" x-small>mdi-check</v-icon></v-chip>
                      <v-chip v-else-if="s.status === 'needs_attention'" color="defaultGrey" text-color="secondary" small>Necesita atención<v-icon class="ml-1" x-small>mdi-clock-outline</v-icon></v-chip>
                    </v-list-item-subtitle>
                    <v-list-item-title class="mt-3 subtitle-2 font-weight-medium">{{firstPlanName(s)}}</v-list-item-title>
                    <v-list-item-subtitle>Próxima facturación {{s.next_billing | date}}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-icon color="secondary">mdi-arrow-right</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </template>
          <v-col v-else class="text-center py-6">
            <v-icon color="secondary" size="70">mdi-autorenew-off</v-icon>
            <span class="d-block subtitle-1">Este documento no tiene suscripciones asociadas</span>
          </v-col>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogPdfViewer" width="700" persistent no-click-animation scrollable>
      <v-card>
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">PDF documento de soporte</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn @click="closePdfDialog" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-row v-if="!pdfViewerUrl">
            <v-col cols="12" class="py-16 text-center">
              <v-icon class="d-block" color="secondary" size="70">mdi-image-broken-variant</v-icon>
              <span class="d-block subtitle-1">PDF no disponible</span>
            </v-col>
          </v-row>
          <v-row v-else no-gutters>
            <v-col cols="12">
              <iframe :src="pdfViewerUrl" width="100%" height="640" style="border: 0;" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn outlined @click="closePdfDialog">Cerrar</v-btn>
          <v-btn v-if="pdfViewerUrl" :href="pdfViewerUrl" target="_blank" color="primary">Abrir en nueva pestaña</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import VTableHeaders from '@/components/VTable/VTableHeaders'
import ListViewMixin from '@/mixins/ListViewMixin'
export default {
  props: {
    actions: {
      type: Array,
      default: () => [
        'disconnect',
        'view_customer',
        'view_pdf',
        'view_subscriptions'
      ]
    },
    hideColumns: {
      type: Array,
      default: () => []
    },
    subscriptionId: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    VTableHeaders
  },
  mixins: [
    ListViewMixin
  ],
  data: () => ({
    associatedSubscriptions: [],
    dialogAssociatedSubscriptions: false,
    dialogPdfViewer: false,
    loadingAssociatedSubscriptions: false,
    pdfViewerUrl: null
  }),
  computed: {
    ...mapState({
      count: state => state.supportingDocuments.documentsCount,
      list: state => state.supportingDocuments.documentsList
    }),
    computedHeaders () {
      const headers = [
        { text: 'Cliente', value: 'customer' },
        { text: 'Tipo', value: 'document_type' },
        { text: '', value: 'subscriptions' },
        { text: 'Número', value: 'serial', align: 'end' },
        { text: 'Fecha', value: 'date_issued', align: 'end', sortable: true },
        { text: 'Vencimiento', value: 'valid_until', align: 'end', sortable: true },
        { text: '', value: 'actions', align: 'end' }
      ]

      // Remove headers that are not included in the columns prop.
      for (let i = headers.length - 1; i >= 0; i--) {
        if (this.hideColumns.includes(headers[i].value)) {
          headers.splice(i, 1)
        }
      }

      return headers
    }
  },
  methods: {
    // Closes the associated subscriptions dialog and clears its content.
    closeAssociatedSubscriptionsDialog () {
      this.dialogAssociatedSubscriptions = false
      this.associatedSubscriptions = []
    },
    // Closes the PDF dialog and resets the current PDF URL.
    closePdfDialog () {
      this.dialogPdfViewer = false
      this.pdfViewerUrl = null
    },
    // Removes the current subscription from the document and refreshes the filtered list.
    disconnectSupportingDocument (document) {
      if (this.subscriptionId === null) {
        return false
      }

      const updatedSubscriptions = this.getDocumentSubscriptionIds(document).filter((id) => id !== String(this.subscriptionId))

      this.$store.dispatch('supportingDocuments/UPDATE', {
        resource: 'supporting-documents/documents',
        id: document.id,
        payload: {
          subscriptions: updatedSubscriptions
        }
      })
      .then(() => {
        this.$dialog.message.info('Documento desconectado correctamente')
        return this.$store.dispatch('supportingDocuments/LIST', {
          resource: 'supporting-documents/documents',
          query: {
            subscriptions: this.subscriptionId,
            expand: 'customer',
            page_size: 100
          },
          setState: true,
          loader: false
        })
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
    },
    // Returns the first plan name for a subscription card.
    firstPlanName (subscription) {
      return subscription?.lines?.[0]?.plan?.name || '---'
    },
    // Normalizes subscription ids from both legacy and many-to-many fields.
    getDocumentSubscriptionIds (document) {
      const ids = []

      if (Array.isArray(document?.subscriptions)) {
        document.subscriptions.forEach((subscription) => {
          if (subscription && typeof subscription === 'object' && subscription.id !== undefined && subscription.id !== null) {
            ids.push(String(subscription.id))
            return
          }

          if (subscription !== undefined && subscription !== null && subscription !== '') {
            ids.push(String(subscription))
          }
        })
      }

      if (document?.subscription !== undefined && document?.subscription !== null && document?.subscription !== '') {
        ids.push(String(document.subscription))
      }

      return Array.from(new Set(ids))
    },
    // Opens the dialog listing subscriptions already linked to the document.
    openAssociatedSubscriptionsDialog (document) {
      const ids = this.getDocumentSubscriptionIds(document)
      this.dialogAssociatedSubscriptions = true
      this.loadingAssociatedSubscriptions = true
      this.associatedSubscriptions = []

      if (!ids.length) {
        this.loadingAssociatedSubscriptions = false
        return false
      }

      this.$store.dispatch('subscriptions/LIST', {
        resource: 'subscriptions',
        query: {
          customer: document.customer.id,
          page_size: 12
        },
        setState: false
      })
      .then((response) => {
        const subscriptions = response.data.results || []
        const selectedIds = new Set(ids.map(String))
        this.associatedSubscriptions = subscriptions.filter((s) => selectedIds.has(String(s.id)))
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
      .finally(() => {
        this.loadingAssociatedSubscriptions = false
      })
    },
    // Opens the document PDF in the embedded viewer dialog.
    openPdf (document) {
      if (!document.file) {
        this.$dialog.message.error('No existe archivo PDF disponible para este documento.')
        return false
      }

      this.pdfViewerUrl = document.file
      this.dialogPdfViewer = true
    }
  }
}
</script>
